// PE-22 K2.3 — the Draft-side twin of programPrescription's
// programWeeklyPrescription (AP-7), computed from the editor's
// in-construction Draft instead of a saved Program, so the composer's
// coverage panel (mockup §03: "Pettorali 18 · Polpacci 0") recolours live
// as the coach edits. Same shape, same math, same averaging rule (Antenore
// 2026-08-27: Σ hard-sets×credits ÷ cycleWeeks, the full-cycle average,
// each round converted to a week by roundsPerWeekOf, #497): a
// coach must never see two different "what does this program train" numbers
// depending on whether they're mid-edit or looking at the saved Home preview.
//
// isPrescribedHardSetByEffort (programPrescription.ts) reads a resolved
// PlannedSet's targetRir/targetRpe. The Draft carries the same decision
// differently: one intensityKind per ROW (a cell may hold its own, kindAt)
// plus one value per WEEK cell. So draftEffortTarget below maps that shape onto the
// SAME shared rule: an explicit 'rir' target is hard only at/below
// EFFORT_HARD_SET_RIR, an explicit 'rpe' target is hard only at/above its
// RPE-scale mirror (10 - EFFORT_HARD_SET_RIR), and every other case —
// percent-of-max / absolute / none / history, OR a rir/rpe row whose cell
// carries no parseable value this week — has no explicit effort target, so
// it counts as a working set: "assumed hard" (same wording as
// isPrescribedHardSetByEffort's own comment).

import {
  amrapOf,
  isCellAbsent,
  kindAt,
  parseFloatLoose,
  type Draft,
  type SetRowDraft,
} from "./programEdit.js";
import type { Exercise } from "./schemas/exercise.js";
import type { CatalogMuscleSlug } from "./muscleCatalog.js";
import {
  amrapStopRirOf,
  cycleAverageByMuscle,
  hardSetsByMuscleByWeek,
  isPrescribedHardSetByEffort,
  roundsPerWeekOf,
  type RoundHardSets,
} from "./programPrescription.js";

// The effort target a Draft cell carries, as a resolved set would: the
// three numbers isPrescribedHardSetByEffort reads. The AMRAP stop (#355) is
// read as the payload writes it: never on a countdown bout.
const draftEffortTarget = (
  row: SetRowDraft,
  week: number,
): { rir: number | null; rpe: number | null; stopRir: number | null } => {
  const cell = row.cells[week];
  const v = parseFloatLoose(cell?.intensityValue ?? "");
  const kind = kindAt(row, week);
  const amrap = cell !== undefined && amrapOf(cell) && row.workKind !== "countdown";
  return {
    rir: kind === "rir" ? v : null,
    rpe: kind === "rpe" ? v : null,
    stopRir: amrapStopRirOf({ amrap, ...(cell?.amrapStop ? { amrapStop: cell.amrapStop } : {}) }),
  };
};

// The Draft's rounds as the shared per-week input (#355), walking the draft's
// own sessions/slots (no macrocycle resolution the way the saved program walks
// resolveFullCycleSessions: the composer's draft-in-construction already
// enumerates every session once; alternating/phase-offset variants are a
// resolved-Program concern this editor doesn't express yet). Skips a week a
// slot opted out of (inactiveWeeks[w]), the same gate
// buildIntensityGridFromDraft applies.
const draftRoundHardSets = (draft: Draft): RoundHardSets[] => {
  const out: RoundHardSets[] = [];
  for (const session of draft.sessions) {
    for (const slot of session.slots) {
      for (let w = 0; w < draft.cycleWeeks; w += 1) {
        if (slot.inactiveWeeks[w]) continue;
        let hard = 0;
        for (const row of slot.setRows) {
          if (isCellAbsent(row, w)) continue;
          const { rir, rpe, stopRir } = draftEffortTarget(row, w);
          if (!isPrescribedHardSetByEffort(rir, rpe, stopRir)) continue;
          hard += 1;
        }
        if (hard > 0) out.push({ weekIdx: w, byExercise: { [slot.exerciseId]: { hard } } });
      }
    }
  }
  return out;
};

// Hard-set coverage per muscle: the per-week walk shared with the saved
// program, averaged over the cycle. Weighted by the same hard-set credit the
// actuals use (resolveExerciseMuscleCredits, #538): a primary muscle earns a
// full set, any other muscle its contribution capped at half of one.
export const hardSetsByMuscleFromDraft = (
  draft: Draft,
  catalog: ReadonlyArray<Exercise>,
): Partial<Record<CatalogMuscleSlug, number>> => {
  const cycleWeeks = draft.cycleWeeks;
  if (!Number.isInteger(cycleWeeks) || cycleWeeks < 1) return {};
  return cycleAverageByMuscle(
    hardSetsByMuscleByWeek(draftRoundHardSets(draft), cycleWeeks, roundsPerWeekOf(draft), catalog)
      .hard,
  );
};
